/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { computed, observable, autorun } from 'mobx';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import ReactPlaceholder from 'react-placeholder';

import Loading from 'components/Loading/Loading';
import navigation from 'services/NavigationService';
import DashboardStore from 'stores/DashboardStore';
import type { TimePeriod } from 'stores/DashboardStore';
import { stringToTimeRange, timeRangeToString, DATE_FORMAT } from 'stores/ReportViewStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { isIE } from 'utils/browserDetection';

import ChartLegend from '../../components/ChartLegend/ChartLegend';
import WidgetActions from '../../components/WidgetActions';
import FilterButtonGroup from '../../components/FilterButtonGroup';
import type { FilterButtonGroupOption } from '../../components/FilterButtonGroup';
import type { CommonWidgetProps } from '../../commonTypes';

import styles from './Calibrations.module.scss';

const STATIC_DATA = [
  {
    assets: 10,
    due_to_expire: 4,
    expired: 6,
    month: 1,
    year: 2021,
    start_date: '2021-01-01 00:00:00',
    end_date: '2021-01-31 23:59:59',
    er_hire_end_date: null,
  },
];

export type CalibrationProps = CommonWidgetProps;

type ChartDataPoint = {
  time: string,
  date_range: string,
  'Upcoming Calibrations': number,
  'Overdue Calibrations': number,
  'Upcoming CalibrationsColor': string,
  'Overdue CalibrationsColor': string,
};

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class Calibrations extends ComponentWithInjectedProps<
  CalibrationProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || Calibrations.LOAD_THUMBNAIL;
  }

  @observable timePeriod: TimePeriod = 'week';

  @computed get currentViewChartData(): ChartDataPoint[] {
    const { t, dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    const upcomingCalibrationsKeyName = t('general:calibrations.upcoming_calibrations');
    const overdueCalibrationsKeyName = t('general:calibrations.overdue_calibrations');
    const CHART_COLORS = {
      [upcomingCalibrationsKeyName + 'Color']: 'hsl(203, 53%, 46%)',
      [overdueCalibrationsKeyName + 'Color']: 'hsl(10, 73%, 45%)',
    };

    const calibrationData =
      dashboardStore.calibrationsByTime && dashboardStore.calibrationsByTime[this.timePeriod]
        ? dashboardStore.calibrationsByTime[this.timePeriod]['upcoming-calibration']
        : null;

    if (calibrationData) {
      switch (this.timePeriod) {
        case 'week': {
          const date = {};
          return calibrationData.map(item => ({
            time: item.date,
            date_range: `${item.date}--${item.date}`,
            [upcomingCalibrationsKeyName]: item ? item.due_to_expire : 0,
            [overdueCalibrationsKeyName]: item ? item.expired : 0,
            ...CHART_COLORS,
          }));
        }

        case 'month': {
          return calibrationData.map(item => {
            const formattedLabel = `Week ${item.week}`;

            return {
              time: formattedLabel,
              date_range: `${item.start_date}--${item.end_date}`,
              [upcomingCalibrationsKeyName]: item ? item.due_to_expire : 0,
              [overdueCalibrationsKeyName]: item ? item.expired : 0,
              ...CHART_COLORS,
            };
          });
        }

        case 'year': {
          return calibrationData.map(item => {
            const formattedLabel = `${item.month}/${item.year}`;

            return {
              time: formattedLabel,
              date_range: `${item.start_date}--${item.end_date}`,
              [upcomingCalibrationsKeyName]: item ? item.due_to_expire : 0,
              [overdueCalibrationsKeyName]: item ? item.expired : 0,
              ...CHART_COLORS,
            };
          });
        }
      }
    }

    return [];
  }

  componentDidMount() {
    autorun(() => {
      const { dashboardStore } = this.props;

      if (this.shouldLoadData) {
        dashboardStore.loadCalibrationsByTime(dashboardStore.loadedTradingAccount, this.timePeriod);
      }
    });
  }

  changeView(timePeriod: TimePeriod) {
    this.timePeriod = timePeriod;
  }

  handleBarClick = (item: Object) => {
    const {
      dashboardStore: { loadedTradingAccount },
    } = this.props;

    const timeRange = stringToTimeRange(item.data.date_range);
    const from = moment(timeRange.from, DATE_FORMAT);
    const to = moment(timeRange.to, DATE_FORMAT);
    const today = moment();
    const todayFormatted = today.format(DATE_FORMAT);

    switch (item.id) {
      case 'Upcoming Calibrations':
        if (today.isAfter(from)) {
          timeRange.from = today.format(DATE_FORMAT);
        }
        break;

      case 'Overdue Calibrations':
        if (today.isBefore(to)) {
          timeRange.to = today.subtract(1, 'day').format(DATE_FORMAT);
        }

        break;
    }

    const filters = [
      {
        key: 'asset_type',
        values: ['Rented Open', 'Purchased', 'Owned'],
      },
      {
        key: 'calibration_due_date',
        values: [timeRangeToString(timeRange.from, timeRange.to)],
      },
    ];

    if (loadedTradingAccount && loadedTradingAccount !== '0') {
      filters.unshift({
        key: 'account',
        values: [loadedTradingAccount],
      });
    }

    navigation.navigateToReportView('/assets', {
      page: 1,
      filters,
      sort: {
        columnName: 'er_calibration_due_date',
        direction: 'ASC',
      },
    });
  };

  renderFullChart = () => {
    const { t, dashboardStore, size } = this.props;
    const margins =
      size === 'thumbnail'
        ? { top: 0, right: 0, bottom: 0, left: 0 }
        : size === 'canvas'
        ? { top: 6, right: 12, bottom: 40, left: 20 }
        : { top: 6, right: 12, bottom: 80, left: 20 };

    const interactive = size === 'thumbnail';
    const gridY = size === 'thumbnail';

    return (
      <React.Fragment>
        <CardContent className={styles['card-content']}>
          <div className={styles['full-container']}>
            <ChartLegend
              position="center"
              keys={[
                {
                  label: t('general:calibrations.upcoming_calibrations'),
                  color: 'hsl(203, 53%, 46%)',
                },
                {
                  label: t('general:calibrations.overdue_calibrations'),
                  color: 'hsl(10, 73%, 45%)',
                },
              ]}
            />
            <div className={styles['bar-chart-container']}>
              {dashboardStore.loadingCalibrationsByTime[this.timePeriod] === true ? (
                <Loading />
              ) : (
                <ResponsiveBar
                  data={this.currentViewChartData}
                  keys={[
                    t('general:calibrations.upcoming_calibrations'),
                    t('general:calibrations.overdue_calibrations'),
                  ]}
                  onClick={this.handleBarClick}
                  indexBy="time"
                  margin={margins}
                  padding={0.2}
                  colorBy={e => e.data[e.id + 'Color']}
                  axisBottom={{
                    orient: 'bottom',
                    tickSize: 0,
                    tickPadding: 15,
                    tickRotation: size === 'canvas-50' ? -90 : 0,
                  }}
                  axisLeft={{
                    renderTick: tick => {
                      return (
                        <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                          <text
                            textAnchor="end"
                            alignmentBaseline="central"
                            transform="translate(-5, 0)"
                          >
                            {Number.isInteger(tick.key) ? tick.key : ''}
                          </text>
                        </g>
                      );
                    },
                  }}
                  enableLabel={false}
                  enableGridY={!gridY}
                  animate={false}
                  motionStiffness={90}
                  motionDamping={15}
                  isInteractive={!interactive}
                />
              )}
            </div>

            <FilterButtonGroup
              onOptionClick={option => {
                if (option)
                  // flow-disable-next-line Bug
                  this.changeView(option.value);
              }}
              value={this.timePeriod}
              options={[
                { label: t('general:labels.week'), value: 'week' },
                { label: t('general:labels.month'), value: 'month' },
                { label: t('general:labels.year'), value: 'year' },
              ]}
            />
          </div>
        </CardContent>
        <div className={styles['button-container']}>
          <Link
            to={navigation.formulateReportViewUri('/assets', {
              page: 1,
              filters: [
                {
                  key: 'asset_type',
                  values: ['Rented Open', 'Purchased', 'Owned'],
                },
                {
                  key: 'calibration_due_date',
                  values: [timeRangeToString(undefined, moment().format(DATE_FORMAT))],
                },
              ],
              sort: {
                columnName: 'er_calibration_due_date',
                direction: 'ASC',
              },
            })}
          >
            <Button color="primary" variant="contained">
              {t('general:assets.view_expired_assets')}
            </Button>
          </Link>
        </div>
      </React.Fragment>
    );
  };

  render() {
    const { dashboardStore, t, widgetItem, size, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    return (
      <Card className={styles[size]}>
        <CardHeader
          title={t('general:calibrations.upcoming_calibrations')}
          action={<WidgetActions widgetItem={widgetItem} size={size} isFullScreen={isFullScreen} />}
          className={styles['card-header']}
          classes={{ action: styles.cardHeaderAction }}
        />
        <Divider />
        {size === 'thumbnail'
          ? !isIE && (
              <div className={styles['thumbnail-container']}>
                <div className={styles['full-container']}>
                  {this.shouldLoadData && dashboardStore.loadingCalibrations ? (
                    <Loading />
                  ) : (
                    <React.Fragment>
                      <div className={styles['half-container']}>
                        <ReactPlaceholder
                          ready={this.shouldLoadData}
                          type="textRow"
                          color="rgba(255, 255, 255, 0.2)"
                          style={{ marginBottom: 20, width: '50%' }}
                        >
                          <span>
                            {this.currentViewChartData
                              ? this.currentViewChartData[0].expired
                              : '...'}
                          </span>
                        </ReactPlaceholder>
                        <small>
                          {this.currentViewChartData && this.currentViewChartData[0].expired
                            ? t('general:assets.overdue_asset', {
                                count: this.currentViewChartData[0].expired,
                              })
                            : ''}
                        </small>
                      </div>
                      <div className={styles['half-container']}>
                        <ReactPlaceholder
                          ready={this.shouldLoadData}
                          type="textRow"
                          color="rgba(255, 255, 255, 0.2)"
                          style={{ marginBottom: 20, width: '50%' }}
                        >
                          <span>
                            {this.currentViewChartData
                              ? this.currentViewChartData[0].assets
                              : '...'}
                          </span>
                        </ReactPlaceholder>

                        <small>{t('general:assets.total_assets')}</small>
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </div>
            )
          : this.renderFullChart()}
      </Card>
    );
  }
}
